import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import { CtNetworkElementType } from '@cloud-twin-v2/cloud-twin-shared/core/network-elements/ct-network-element-type';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import {
  AttributeInfoDto,
  LayerThematicStylesDto,
  LayerType,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { take } from 'rxjs/operators';

const COMPONENT_SELECTOR = 'wlm-new-thematic-popup';
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './new-thematic-popup.component.html',
  styleUrl: './new-thematic-popup.component.scss',
  standalone: false,
})
export class NewThematicPopupComponent implements OnInit {
  @Input() isEditMode = false;

  get initialModel(): LayerThematicStylesDto {
    return this._initialModel;
  }
  @Input() set initialModel(value: LayerThematicStylesDto) {
    if (!value) return;

    value.workspaceId = this._selectedWorkspaceService.selectedWorkspace?.workspaceId;
    this._initialModel = value;
    if (!this._networkElementTypesLoaded) {
      this.getNetworkElementTypes();
      this._networkElementTypesLoaded = true;
    } else {
      if (this.initialModel?.objectType && Object.keys(this.metadata).length > 0) {
        this.onNeTypeChange(this.initialModel.objectType, true);
      }
    }

    if (this._initialModel.fieldName) {
      this.setAttributeFlags(this._initialModel.fieldName);
      this.attributeIsNumeric = true;
    } else {
      this.attributeIsNumeric = false;
      this.uom = '';
    }
    this.validateInputs();
  }

  @Output() popupClose = new EventEmitter<any>();

  private _networkElementTypesLoaded = false;
  private _initialModel: LayerThematicStylesDto = new LayerThematicStylesDto();
  readonly T_SCOPE = `${AppModules.Map}.${COMPONENT_SELECTOR}`;
  private readonly _ctAttributeService = inject(CtAttributesService);
  private readonly _selectedWorkspaceService = inject(SelectedWorkspaceService);

  selectedLayerType: LayerType | null = null;
  neTypes: CtNetworkElementType[] = [];
  uom: string;
  duplicatedUom: string;
  isValid: boolean = false;
  metadata: CtMetadata = {};
  attributeIsNumeric: boolean = false;
  isThematicConfiguratorPopupClosed: boolean = true;
  selectedGeometryType: string;
  model: LayerThematicStylesDto;
  selectedLayer: string;

  private _attributeOptions: AttributeInfoDto[] = [];

  get attributeOptions(): AttributeInfoDto[] {
    return this._attributeOptions;
  }

  set attributeOptions(value: AttributeInfoDto[]) {
    this._attributeOptions = value.sort((a, b) => {
      return a.alias.localeCompare(b.alias);
    });
  }

  readonly layerTypes = [
    { type: LayerType.Point, label: this.T_SCOPE + '.style-items.point-graduate' },
    { type: LayerType.Line, label: this.T_SCOPE + '.style-items.line-graduate' },
    { type: LayerType.Polygon, label: this.T_SCOPE + '.style-items.polygon-graduate' },
  ];

  ngOnInit(): void {
    this._ctAttributeService.metadata$.pipe(take(1)).subscribe((metadata) => {
      this.metadata = metadata;
    });
    this.getNetworkElementTypes();
  }

  closePopup(): void {
    this.popupClose.emit();
  }

  updateModelField<K extends keyof LayerThematicStylesDto>(
    key: K,
    value: LayerThematicStylesDto[K]
  ) {
    this.initialModel = new LayerThematicStylesDto({
      ...this.initialModel,
      [key]: value,
    });
    this.model = new LayerThematicStylesDto(this.deepClone(this.initialModel));

    this.validateInputs();
  }

  getNetworkElementTypes() {
    this._ctAttributeService
      .getNetworkElementTypes()
      .pipe(take(1))
      .subscribe((types: CtNetworkElementType[]) => {
        this.neTypes = types;
        if (this.initialModel?.objectType && Object.keys(this.metadata).length > 0) {
          this.onNeTypeChange(this.initialModel.objectType, true);
        }
      });
  }

  onNeTypeChange(neTypeId: string, skipUpdateModel = false) {
    if (!skipUpdateModel) {
      this.updateModelField('objectType', neTypeId);
    }
    this.validateInputs();

    const selectedType = this.neTypes.find((ne) => ne.networkElementTypeId === neTypeId);
    const selectedTypeName = selectedType?.networkElementTypeName;

    let attributes: AttributeInfoDto[] = [];
    for (const scenarioId in this.metadata) {
      const categoriesArr = this.metadata[scenarioId];

      const matching = categoriesArr.filter((cat) => cat.type === selectedTypeName);

      for (const cat of matching) {
        this.selectedGeometryType = cat.geometryType;
        if (cat.categories) {
          for (const category of cat.categories) {
            if (category.attributes) {
              attributes.push(...category.attributes);
              const allNumeric = attributes.every(
                (attr) => attr.attributeTypeKey === 'Double' || attr.attributeTypeKey === 'Int64'
              );

              if (!allNumeric) {
                this.isThematicConfiguratorPopupClosed = true;
              }
            }
          }
        }
      }
    }

    this.attributeOptions = attributes.filter(
      (attr, idx, arr) => arr.findIndex((a) => a.name === attr.name) === idx
    );
  }

  onAttributeChange(attrName: string) {
    const selectedAttr = this.attributeOptions.find((attr) => attr.name === attrName);
    this.attributeIsNumeric = selectedAttr
      ? selectedAttr.attributeTypeKey === 'Double' || selectedAttr.attributeTypeKey === 'Int64'
      : false;
    this.uom = selectedAttr?.units || '';

    if (!this.attributeIsNumeric) {
      this.isThematicConfiguratorPopupClosed = true;
    }
    this.updateModelField('fieldName', attrName);
  }

  validateInputs() {
    this.isValid = !!(
      this.initialModel.styleName &&
      this.initialModel.objectType &&
      this.initialModel.fieldName &&
      this.initialModel.layerType
    );
  }

  closeNewThematicPopup() {
    this.isThematicConfiguratorPopupClosed = true;
  }

  onLayerTypeSelect(type: LayerType) {
    this.initialModel.layerType = type;
    this.updateModelField('layerType', type);
    this.isThematicConfiguratorPopupClosed = false;
  }

  private setAttributeFlags(fieldName: string) {
    const selectedAttr = this.attributeOptions.find((attr) => attr.name === fieldName);
    this.attributeIsNumeric = selectedAttr
      ? selectedAttr.attributeTypeKey === 'Double' || selectedAttr.attributeTypeKey === 'Int64'
      : false;
    this.uom = selectedAttr?.units || '';

    if (!this.attributeIsNumeric) {
      this.isThematicConfiguratorPopupClosed = true;
    }
  }

  private deepClone(obj: any): any {
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map((item) => this.deepClone(item));
    const cloned: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = this.deepClone(obj[key]);
      }
    }
    return cloned;
  }

  get filteredLayerTypes(): any[] {
    if (!this.selectedGeometryType) return this.layerTypes;
    if (this.selectedGeometryType === 'Point') {
      return this.layerTypes.filter((lt) => lt.type === LayerType.Point);
    }
    if (this.selectedGeometryType === 'Polygon') {
      return this.layerTypes.filter((lt) => lt.type === LayerType.Polygon);
    }
    if (this.selectedGeometryType === 'LineString' || this.selectedGeometryType === 'Line') {
      return this.layerTypes.filter((lt) => lt.type === LayerType.Line);
    }
    return this.layerTypes;
  }
}
